import { getSavedBandwidthEstimate } from '~/composables/useHlsPlayer/shakaLoader';

// Teto de cadeias de video simultaneas: sem ele uma grade de desktop abre ~12
// competindo com o card em foco.

/**
 * Distancia do foco em cards (0 = olhado). Numero, e nao 0|1: a janela
 * direcional ordena vizinhos entre si.
 */
export type PreloadDistance = number;

interface Holder {
	distance: PreloadDistance;
	evict: () => void;
}

interface Waiter {
	id: string;
	distance: PreloadDistance;
	start: () => void;
	evict: () => void;
}

const holders = new Map<string, Holder>();
const waiting: Waiter[] = [];

// Mobile mostra 1-2 cards e costuma estar na pior rede, entao teto menor.
const DESKTOP_SLOTS = 3;
const MOBILE_SLOTS = 2;
const SLOW_NETWORK_SLOTS = 1;
const SLOW_NETWORK_BPS = 2_000_000;
/** Abaixo de lg o layout e de uma coluna (mesmo corte que os carrosseis usam). */
const MOBILE_MAX_WIDTH = 1024;

// Sem evidencia de intencao nesta tela o teto especulativo e zero. Autoplay NAO
// conta como sinal, senao destravaria a especulacao nos vizinhos.
export const hasCardPreloadSignal = ref(false);

const SIGNAL_EVENTS = [
	'pointerdown',
	'touchstart',
	'wheel',
	'keydown',
	'scroll'
] as const;
let signalListenersArmed = false;

export function notifyCardPreloadSignal(): void {
	if (!import.meta.client || hasCardPreloadSignal.value) return;
	hasCardPreloadSignal.value = true;
	while (waiting.length > 0 && holders.size < slotLimit()) admitFromQueue();
}

function armSignalListeners(): void {
	if (!import.meta.client || signalListenersArmed) return;
	signalListenersArmed = true;
	for (const type of SIGNAL_EVENTS) {
		window.addEventListener(type, notifyCardPreloadSignal, {
			once: true,
			passive: true
		});
	}
}

function slotLimit(): number {
	if (!import.meta.client) return 0;
	if (!hasCardPreloadSignal.value) return 0;
	const bps = getSavedBandwidthEstimate();
	// Estimativa desconhecida (primeira visita) NAO e tratada como lenta.
	if (bps !== null && bps < SLOW_NETWORK_BPS) return SLOW_NETWORK_SLOTS;
	return window.innerWidth < MOBILE_MAX_WIDTH ? MOBILE_SLOTS : DESKTOP_SLOTS;
}

function admitFromQueue(): void {
	if (waiting.length === 0 || holders.size >= slotLimit()) return;
	// Mais proximo do foco primeiro; empate resolve por ordem de chegada.
	let bestAt = 0;
	for (let at = 1; at < waiting.length; at++) {
		if (waiting[at]!.distance < waiting[bestAt]!.distance) bestAt = at;
	}
	const next = waiting.splice(bestAt, 1)[0]!;
	holders.set(next.id, { distance: next.distance, evict: next.evict });
	next.start();
}

/** Lotado: o ocupante mais longe do foco cede; empate vira fila. */
export function requestCardPreload(
	id: string,
	distance: PreloadDistance,
	start: () => void,
	evict: () => void
): void {
	if (!import.meta.client) return;
	armSignalListeners();
	// Ja tem vaga (o observer redispara a cada mudanca de ratio): so atualiza.
	const current = holders.get(id);
	if (current) {
		current.distance = distance;
		return;
	}

	const queuedAt = waiting.findIndex(w => w.id === id);
	if (queuedAt !== -1) {
		waiting[queuedAt]!.distance = distance;
		waiting[queuedAt]!.start = start;
		waiting[queuedAt]!.evict = evict;
		admitFromQueue();
		return;
	}

	if (holders.size < slotLimit()) {
		holders.set(id, { distance, evict });
		start();
		return;
	}

	let farthestId: string | null = null;
	let farthestDistance = distance;
	for (const [holderId, holder] of holders) {
		if (holder.distance > farthestDistance) {
			farthestId = holderId;
			farthestDistance = holder.distance;
		}
	}
	if (farthestId !== null) {
		const evicted = holders.get(farthestId)!;
		holders.delete(farthestId);
		evicted.evict();
		holders.set(id, { distance, evict });
		start();
		return;
	}

	waiting.push({ id, distance, start, evict });
}

export function releaseCardPreload(id: string): void {
	if (!import.meta.client) return;
	const queuedAt = waiting.findIndex(w => w.id === id);
	if (queuedAt !== -1) waiting.splice(queuedAt, 1);
	if (holders.delete(id)) admitFromQueue();
}

export function resetCardPreloadBudget(): void {
	holders.clear();
	waiting.length = 0;
	hasCardPreloadSignal.value = false;
	signalListenersArmed = false;
}
